import { Router } from "express";
import { db } from "@workspace/db";
import { savedSearchesTable, tendersTable } from "@workspace/db";
import { eq, and, or } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";

const router = Router();

router.get("/saved-searches", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const searches = await db
      .select()
      .from(savedSearchesTable)
      .where(eq(savedSearchesTable.companyId, req.companyId))
      .orderBy(savedSearchesTable.createdAt);
    res.json(searches);
  } catch (err) {
    next(err);
  }
});

router.post("/saved-searches", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const { name, filters, isWatchlist } = req.body;
    const saved = await db
      .insert(savedSearchesTable)
      .values({
        companyId: req.companyId,
        userId: req.dbUser?.id,
        name,
        filters: filters ?? {},
        isWatchlist: isWatchlist ? 1 : 0,
      })
      .returning();
    res.status(201).json(saved[0]);
  } catch (err) {
    next(err);
  }
});

router.delete("/saved-searches/:id", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    await db
      .delete(savedSearchesTable)
      .where(and(eq(savedSearchesTable.id, Number(req.params.id)), eq(savedSearchesTable.companyId, req.companyId)));
    res.status(204).send();
  } catch (err) {
    next(err);
  }
});

router.get("/watchlist", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const watchlists = await db
      .select()
      .from(savedSearchesTable)
      .where(and(eq(savedSearchesTable.companyId, req.companyId), eq(savedSearchesTable.isWatchlist, 1)));

    const results: unknown[] = [];
    for (const w of watchlists) {
      const filters = (w.filters ?? {}) as Record<string, unknown>;
      const tenderIds = (filters.tenderIds ?? []) as number[];
      if (tenderIds.length === 0) continue;
      const tenders = await db.select().from(tendersTable)
        .where(and(eq(tendersTable.companyId, req.companyId)));
      results.push({ watchlist: w, tenders: tenders.filter((t) => tenderIds.includes(t.id)) });
    }
    res.json(results);
  } catch (err) {
    next(err);
  }
});

/* ── Marketplace saved searches (isWatchlist = 2) ────────── */
router.get("/marketplace/saved-searches", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const userId = req.dbUser!.id;
    const searches = await db
      .select()
      .from(savedSearchesTable)
      .where(and(
        eq(savedSearchesTable.companyId, req.companyId),
        eq(savedSearchesTable.userId, userId),
        eq(savedSearchesTable.isWatchlist, 2),
      ))
      .orderBy(savedSearchesTable.createdAt);
    res.json(searches);
  } catch (err) { next(err); }
});

router.post("/marketplace/saved-searches", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const userId = req.dbUser!.id;
    const { name, filters } = req.body;
    if (!name?.trim()) return res.status(400).json({ error: "name is required" });
    const [saved] = await db
      .insert(savedSearchesTable)
      .values({ companyId: req.companyId, userId, name: name.trim(), filters: filters ?? {}, isWatchlist: 2 })
      .returning();
    res.status(201).json(saved);
  } catch (err) { next(err); }
});

router.delete("/marketplace/saved-searches/:id", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const userId = req.dbUser!.id;
    await db
      .delete(savedSearchesTable)
      .where(and(
        eq(savedSearchesTable.id, Number(req.params.id)),
        eq(savedSearchesTable.companyId, req.companyId),
        eq(savedSearchesTable.userId, userId),
        eq(savedSearchesTable.isWatchlist, 2),
      ));
    res.status(204).send();
  } catch (err) { next(err); }
});

export default router;
